import store from '@/store';
import TagsTypesService from '@Tags/services/tagsTypes.service';
import Vue from 'vue';
import permissions from '@Share/config/permissions';
import { $has } from '@/utils';
import router from '@/router';

// Tag types that don't have list views and should return to main panel when backing out
const TAG_TYPES_WITHOUT_LIST = ['AvaInspect'];

export const TAGS_TYPES = {
  namespaced: true,
  state: {
    selectedType: null,
    tagsTypesCounts: [],
    tagsTypesCountsLoading: false,
    openedTreeNodes: [],
    backToGlobalSearch: false,
    // Tracks where the tag was opened from (e.g., 'condition-map', 'global-search', 'tags')
    // Used to determine correct back navigation destination
    sourcePanel: null,
    // AbortController for cancelling pending requests
    currentRequestController: null,
  },
  getters: {
    GET_SELECTED_TYPE(state) {
      return state.selectedType;
    },
    GET_TAGS_TYPES_COUNTS(state) {
      return state.tagsTypesCounts;
    },
    GET_TAGS_TYPES_COUNTS_LOADING(state) {
      return state.tagsTypesCountsLoading;
    },
    GET_OPENED_TREE_NODES(state) {
      return state.openedTreeNodes;
    },
    GET_BACK_TO_GLOBAL_SEARCH(state) {
      return state.backToGlobalSearch;
    },
    GET_SOURCE_PANEL(state) {
      return state.sourcePanel;
    },
    GET_CURRENT_REQUEST_CONTROLLER(state) {
      return state.currentRequestController;
    },
  },
  mutations: {
    SET_SELECTED_TYPE(state, val) {
      state.selectedType = val;
    },
    SET_TAGS_TYPES_COUNTS(state, val) {
      state.tagsTypesCounts = val;
    },
    SET_TAGS_TYPES_COUNTS_LOADING(state, val) {
      state.tagsTypesCountsLoading = val;
    },
    SET_OPENED_TREE_NODES(state, val) {
      state.openedTreeNodes = val;
    },
    SET_BACK_TO_GLOBAL_SEARCH(state, val) {
      state.backToGlobalSearch = val;
    },
    SET_SOURCE_PANEL(state, val) {
      state.sourcePanel = val;
    },
    SET_REQUEST_CONTROLLER(state, controller) {
      state.currentRequestController = controller;
    },
    CLEAR_REQUEST_CONTROLLER(state) {
      state.currentRequestController = null;
    },
  },
  actions: {
    /**
     * Fetch tags types counts with cancellation support
     * Automatically cancels any pending request before starting a new one
     */
    async GET_TAGS_TYPES_COUNTS({ commit, state }) {
      // Cancel any existing request before starting a new one
      if (state.currentRequestController) {
        state.currentRequestController.abort();
      }

      // Create new AbortController for this request
      const controller = new AbortController();
      commit('SET_REQUEST_CONTROLLER', controller);
      commit("SET_TAGS_TYPES_COUNTS_LOADING", true);

      let assetId = store.getters["FACILITY/GET_FACILITY"]?.id ?? router.currentRoute.params.id;

      let types = [];

      if ($has(permissions.Tags.Anomaly.View)) {
        types.push('Anomaly');
      }

      if ($has(permissions.Tags.Equipment.View)) {
        types.push('Equipment');
      }

      if ($has(permissions.Tags.Pipeline.View)) {
        types.push('Pipeline');
      }

      if ($has(permissions.Tags.Structural.View)) {
        types.push('Structural');
      }

      // AvaInspect removed from types selector - will be displayed in separate location
      // if ($has(permissions.Tags.AvaInspect?.View)) {
      //   types.push('AvaInspect');
      // }

      if ($has(permissions.Tags.Scaffold.View)) {
        types.push('Scaffold');
      }

      if ($has(permissions.Tags.General.View)) {
        types.push('General');
      }

      if ($has(permissions.Tags.Permit.View)) {
        types.push('Permit');
      }

      if ($has(permissions.Tags.Container.View)) {
        types.push('Container');
      }

      try {
        const { data } = await TagsTypesService.get({ assetId, types }, controller.signal);
        commit("SET_TAGS_TYPES_COUNTS", data);
      } catch (err) {
        // Don't show error for cancelled requests
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          Vue.prototype.$dialog.notify.error("Failed to fetch tags types counts");
        }
      } finally {
        commit("SET_TAGS_TYPES_COUNTS_LOADING", false);
        commit('CLEAR_REQUEST_CONTROLLER');
      }
    },

    /**
     * Cancel any pending request
     * Called when component is destroyed or navigation occurs
     */
    CANCEL_CURRENT_REQUEST({ commit, state }) {
      if (state.currentRequestController) {
        state.currentRequestController.abort();
        commit('CLEAR_REQUEST_CONTROLLER');
        commit("SET_TAGS_TYPES_COUNTS_LOADING", false);
      }
    },
    ONE_STEP_BACK({ commit, getters }) {
      let previousTags = store.getters['GET_PREVIOUS_TAGS'];
      let editElement = store.getters['GET_EDITING_TAG'];
      let isReadOnly = store.getters['GET_IS_TAG_READ_ONLY'];
      let selectedType = getters['GET_SELECTED_TYPE'];
      let selectedGroup = store.getters['TAGS_GROUPS/GET_SELECTED_GROUP'];
      let backToGlobalSearch = getters['GET_BACK_TO_GLOBAL_SEARCH'];
      let sourcePanel = getters['GET_SOURCE_PANEL'];

      if (!previousTags.length && !editElement && !selectedType && !selectedGroup && !backToGlobalSearch) {
        store.dispatch('closeLeftTab');
        return;
      }

      if (previousTags.length) {
        store.dispatch('POP_TAG_FROM_PREVIOUS_TAGS');
      } else if (editElement) {
        if (isReadOnly) {
          store.dispatch('CLEAR_EDIT_TAG_TAB');

          // If tag type doesn't have a list view, clear selectedType to return to main panel
          if (TAG_TYPES_WITHOUT_LIST.includes(selectedType)) {
            commit('SET_SELECTED_TYPE', null);
          }

          // Handle navigation based on source panel
          if (sourcePanel === 'condition-map') {
            // Tag was opened from Condition Map - return there
            commit('SET_SOURCE_PANEL', null);
            commit('SET_SELECTED_TYPE', null);
            store.dispatch('GENERAL/CLOSE_PANEL', 'tags', { root: true });
            store.dispatch('GENERAL/OPEN_PANEL', 'condition-map', { root: true });
            return;
          }

          if (backToGlobalSearch) {
            commit('SET_BACK_TO_GLOBAL_SEARCH', false);
            store.state.mainApp.openGlobalSearch();
          }
        } else {
          Vue.prototype.$dialog.confirm({
            text: 'Do you want to discard changes?',
            title: 'Save Changes',
            actions: {
              false: {
                text: 'Yes',
                color: 'error',
                handle: () => {
                  store.commit('SET_IS_TAG_READ_ONLY', true);
                },
              },
              true: {
                text: 'No',
                color: 'primary'
              },
            },
          });
        }
      } else {
        commit('SET_SELECTED_TYPE', null);
        store.commit('TAGS_GROUPS/SET_SELECTED_GROUP', null);
      }
    },
  },
}